// Loader.tsx
// Based on work by ikesau, 2019-05-30
// Copyright (c) 2019 Element AI Inc. All rights reserved.
import { styled } from "@mui/material/styles";
import React, { useEffect, useState } from "react";

import styles from "./Loader.module.scss";

const StyledRect = styled("rect")(({ theme }) => ({
  stroke: theme.palette.primary.main,
}));

type Props = {
  loading: boolean;
  className?: string;
  loaderClassName?: string;
  slowRequestDuration?: number;
  slowRequestWarningMessage?: string;
};

const Loader = React.memo(function Loader({
  loading,
  className = "",
  loaderClassName = "",
  slowRequestDuration,
  slowRequestWarningMessage = "This is taking longer than expected but we are getting there.",
}: Props) {
  const [isSlowRequest, setSlowRequest] = useState(false);

  useEffect(() => {
    if (slowRequestDuration) {
      const timer = setTimeout(() => setSlowRequest(true), slowRequestDuration);
      return () => clearTimeout(timer);
    }
    return;
  }, [slowRequestDuration, setSlowRequest]);

  return (
    <div
      hidden={!loading}
      className={`${styles.loadedContentContainer} ${className}`}
    >
      <div className={`${styles.loaderContainer} ${loaderClassName}`}>
        <svg viewBox="0 0 16 16">
          <StyledRect className={styles.loaderRect} x={1} y={1} />
        </svg>
        {isSlowRequest && (
          <p data-cy="loader-warning-message">{slowRequestWarningMessage}</p>
        )}
      </div>
    </div>
  );
});

const LoadOverlay = styled(Loader)(() => ({
  position: "absolute",
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
}));

export { Loader, LoadOverlay };
